// frontend/src/pages/admin/AdminDashboard.jsx
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchUsers, createUser, updateUser, deleteUser, resetUserPassword,
  fetchRoles, fetchPermissions, createRole, updateRole, deleteRole,
  fetchGroups, createGroup, updateGroup, deleteGroup, addGroupMember, removeGroupMember,
  fetchPoleTypes, createPoleType, updatePoleType, deletePoleType
} from "../../services/api";

// ── Shared styles
const S = {
  page:    { minHeight:"100vh", background:"#0a0f1a", fontFamily:"'JetBrains Mono',monospace", color:"#e2e8f0" },
  card:    { background:"#0d1520", border:"1px solid #1a2e1a", borderRadius:12, padding:20, marginBottom:16 },
  th:      { padding:"10px 14px", fontSize:11, color:"#64748b", letterSpacing:"0.1em", borderBottom:"1px solid #1a2e1a", textAlign:"left" },
  td:      { padding:"10px 14px", fontSize:12, borderBottom:"1px solid #0d1a0d", verticalAlign:"middle" },
  input:   { width:"100%", background:"#0a1628", border:"1px solid #1e3a2e", borderRadius:8, padding:"8px 12px", color:"#e2e8f0", fontSize:13, outline:"none", boxSizing:"border-box", marginBottom:12 },
  select:  { width:"100%", background:"#0a1628", border:"1px solid #1e3a2e", borderRadius:8, padding:"8px 12px", color:"#e2e8f0", fontSize:13, outline:"none", boxSizing:"border-box", marginBottom:12 },
  label:   { fontSize:10, color:"#64748b", display:"block", marginBottom:4, letterSpacing:"0.1em" },
  btn:     (color="#16a34a") => ({ background:color, color:"#fff", border:"none", borderRadius:8, padding:"8px 16px", fontSize:12, fontWeight:700, cursor:"pointer" }),
  badge:   (color="#22c55e") => ({ fontSize:10, padding:"2px 8px", borderRadius:99, border:`1px solid ${color}40`, background:`${color}18`, color, display:"inline-block" }),
};

function Modal({ title, onClose, children }) {
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"#0d1520", border:"1px solid #1a2e1a", borderRadius:16, padding:24, width:"100%", maxWidth:500, maxHeight:"90vh", overflowY:"auto" }}>
        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:16, fontWeight:800, color:"#4ade80", marginBottom:20 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

function Msg({ text }) {
  if (!text) return null;
  const ok = text.includes("success") || text.includes("✅");
  return (
    <div style={{ padding:"8px 12px", borderRadius:8, marginBottom:12, fontSize:12,
      background: ok ? "#14532d" : "#450a0a", color: ok ? "#86efac" : "#fca5a5",
      border:`1px solid ${ok ? "#22c55e" : "#7f1d1d"}` }}>{text}</div>
  );
}

// ════════════════════════════════════════════════════════════
// USERS TAB
// ════════════════════════════════════════════════════════════
function UsersTab({ roles, allUsers }) {
  const [users, setUsers]           = useState([]);
  const [showForm, setShowForm]     = useState(false);
  const [editing, setEditing]       = useState(null);
  const [resetUser, setResetUser]   = useState(null);
  const [deleteTarget, setDelete]   = useState(null);
  const [msg, setMsg]               = useState("");
  const [search, setSearch]         = useState("");
  const emptyForm = { username:"", full_name:"", email:"", password:"", role_id:"" };
  const [form, setForm]             = useState(emptyForm);
  const [resetPw, setResetPw]       = useState("");

  const load = useCallback(() => {
    fetchUsers().then(r => setUsers(r.data.data)).catch(console.error);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setMsg("");
    try {
      if (editing) { await updateUser(editing.id, form); setMsg("✅ User updated successfully!"); }
      else         { await createUser(form);              setMsg("✅ User created successfully!"); }
      load();
      setTimeout(() => { setShowForm(false); setEditing(null); setForm(emptyForm); setMsg(""); }, 1500);
    } catch(err) { setMsg(err.response?.data?.message || "Failed to save user"); }
  };

  const doDelete = async () => {
    try { await deleteUser(deleteTarget.id); load(); setDelete(null); }
    catch(err) { setMsg(err.response?.data?.message || "Failed to delete"); }
  };

  const doReset = async () => {
    if (!resetPw || resetPw.length < 6) return setMsg("Password must be at least 6 characters");
    try {
      await resetUserPassword(resetUser.id, resetPw);
      setMsg("✅ Password reset successfully!");
      setTimeout(() => { setResetUser(null); setResetPw(""); setMsg(""); }, 1500);
    } catch(err) { setMsg(err.response?.data?.message || "Failed to reset password"); }
  };

  const filtered = users.filter(u =>
    u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.username?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, color:"#4ade80" }}>👥 Users</div>
          <div style={{ fontSize:11, color:"#4b5563" }}>{users.length} total users</div>
        </div>
        <button style={S.btn()} onClick={() => { setEditing(null); setForm(emptyForm); setMsg(""); setShowForm(true); }}>
          ➕ Add User
        </button>
      </div>

      <input placeholder="🔍 Search users..." value={search} onChange={e=>setSearch(e.target.value)}
        style={{ ...S.input, marginBottom:16 }} />

      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:"#060c12" }}>
              {["Full Name","Username","Email","Role","Groups","Status","Actions"].map(h=>(
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} style={{ background: u.id % 2 === 0 ? "rgba(255,255,255,0.01)" : "transparent" }}>
                <td style={S.td}><span style={{ fontWeight:600 }}>{u.full_name}</span></td>
                <td style={S.td}><span style={{ color:"#94a3b8" }}>@{u.username}</span></td>
                <td style={S.td}><span style={{ color:"#64748b", fontSize:11 }}>{u.email}</span></td>
                <td style={S.td}>
                  {u.role_label
                    ? <span style={S.badge(u.role_color || "#22c55e")}>{u.role_label}</span>
                    : <span style={{ color:"#4b5563", fontSize:11 }}>No role</span>}
                </td>
                <td style={S.td}><span style={{ color:"#64748b", fontSize:11 }}>{u.groups || "—"}</span></td>
                <td style={S.td}>
                  <span style={S.badge(u.is_active ? "#22c55e" : "#6b7280")}>
                    {u.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td style={S.td}>
                  <div style={{ display:"flex", gap:4 }}>
                    <button style={{ ...S.btn("#1e3a5f"), padding:"4px 8px", fontSize:10 }}
                      onClick={() => { setEditing(u); setForm({ username:u.username, full_name:u.full_name, email:u.email, password:"", role_id:u.role_id||"", is_active:u.is_active }); setMsg(""); setShowForm(true); }}>
                      ✏️ Edit
                    </button>
                    <button style={{ ...S.btn("#4a1d96"), padding:"4px 8px", fontSize:10 }}
                      onClick={() => { setResetUser(u); setResetPw(""); setMsg(""); }}>
                      🔑 Reset
                    </button>
                    <button style={{ ...S.btn("#450a0a"), padding:"4px 8px", fontSize:10 }}
                      onClick={() => setDelete(u)}>
                      🗑
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div style={{ textAlign:"center", color:"#4b5563", padding:32, fontSize:13 }}>No users found</div>}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <Modal title={editing ? "✏️ Edit User" : "➕ Add New User"} onClose={() => setShowForm(false)}>
          <label style={S.label}>FULL NAME</label>
          <input style={S.input} value={form.full_name} onChange={e=>setForm(p=>({...p, full_name:e.target.value}))} />
          <label style={S.label}>USERNAME</label>
          <input style={S.input} value={form.username} onChange={e=>setForm(p=>({...p, username:e.target.value}))} disabled={!!editing} />
          <label style={S.label}>EMAIL</label>
          <input style={S.input} value={form.email} onChange={e=>setForm(p=>({...p, email:e.target.value}))} />
          {!editing && (<>
            <label style={S.label}>PASSWORD</label>
            <input style={S.input} type="password" value={form.password} onChange={e=>setForm(p=>({...p, password:e.target.value}))} placeholder="Min 6 characters" />
          </>)}
          <label style={S.label}>ROLE</label>
          <select style={S.select} value={form.role_id} onChange={e=>setForm(p=>({...p, role_id:e.target.value}))}>
            <option value="">— Select Role —</option>
            {roles.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
          {editing && (<>
            <label style={S.label}>STATUS</label>
            <select style={S.select} value={form.is_active ? "1" : "0"} onChange={e=>setForm(p=>({...p, is_active:e.target.value==="1"}))}>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </>)}
          <Msg text={msg} />
          <div style={{ display:"flex", gap:8 }}>
            <button style={{ ...S.btn("#1e2d3d"), flex:1 }} onClick={() => setShowForm(false)}>Cancel</button>
            <button style={{ ...S.btn(), flex:1 }} onClick={save}>{editing ? "Update User" : "Create User"}</button>
          </div>
        </Modal>
      )}

      {/* Reset Password Modal */}
      {resetUser && (
        <Modal title="🔑 Reset Password" onClose={() => setResetUser(null)}>
          <p style={{ color:"#94a3b8", fontSize:13, marginBottom:16 }}>
            Reset password for <strong style={{ color:"#e2e8f0" }}>{resetUser.full_name}</strong>
          </p>
          <label style={S.label}>NEW PASSWORD</label>
          <input style={S.input} type="password" value={resetPw} onChange={e=>setResetPw(e.target.value)} placeholder="Min 6 characters" />
          <Msg text={msg} />
          <div style={{ display:"flex", gap:8 }}>
            <button style={{ ...S.btn("#1e2d3d"), flex:1 }} onClick={() => setResetUser(null)}>Cancel</button>
            <button style={{ ...S.btn(), flex:1 }} onClick={doReset}>Reset Password</button>
          </div>
        </Modal>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <Modal title="🗑 Delete User" onClose={() => setDelete(null)}>
          <p style={{ color:"#94a3b8", fontSize:13, marginBottom:20 }}>
            Delete <strong style={{ color:"#e2e8f0" }}>{deleteTarget.full_name}</strong>? This cannot be undone.
          </p>
          <div style={{ display:"flex", gap:8 }}>
            <button style={{ ...S.btn("#1e2d3d"), flex:1 }} onClick={() => setDelete(null)}>Cancel</button>
            <button style={{ ...S.btn("#7f1d1d"), flex:1 }} onClick={doDelete}>Yes, Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// ROLES TAB
// ════════════════════════════════════════════════════════════
function RolesTab({ onRolesChange }) {
  const [roles, setRoles]           = useState([]);
  const [permissions, setPerms]     = useState([]);
  const [showForm, setShowForm]     = useState(false);
  const [editing, setEditing]       = useState(null);
  const [deleteTarget, setDelete]   = useState(null);
  const [msg, setMsg]               = useState("");
  const emptyForm = { label:"", color:"#22c55e", description:"", permissions:[] };
  const [form, setForm]             = useState(emptyForm);

  const load = useCallback(() => {
    fetchRoles().then(r => { setRoles(r.data.data); onRolesChange(r.data.data); }).catch(console.error);
    fetchPermissions().then(r => setPerms(r.data.data)).catch(console.error);
  }, [onRolesChange]);

  useEffect(() => { load(); }, [load]);

  const togglePerm = (pid) => {
    setForm(p => ({
      ...p,
      permissions: p.permissions.includes(pid)
        ? p.permissions.filter(x => x !== pid)
        : [...p.permissions, pid]
    }));
  };

  const save = async () => {
    setMsg("");
    try {
      if (editing) { await updateRole(editing.id, form); setMsg("✅ Role updated!"); }
      else         { await createRole(form);              setMsg("✅ Role created!"); }
      load();
      setTimeout(() => { setShowForm(false); setEditing(null); setForm(emptyForm); setMsg(""); }, 1500);
    } catch(err) { setMsg(err.response?.data?.message || "Failed to save role"); }
  };

  const doDelete = async () => {
    try { await deleteRole(deleteTarget.id); load(); setDelete(null); }
    catch(err) { setMsg(err.response?.data?.message || "Cannot delete system role"); setDelete(null); }
  };

  // Group permissions by category
  const permsByCategory = permissions.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, color:"#4ade80" }}>🔑 Roles</div>
          <div style={{ fontSize:11, color:"#4b5563" }}>{roles.length} roles configured</div>
        </div>
        <button style={S.btn()} onClick={() => { setEditing(null); setForm(emptyForm); setMsg(""); setShowForm(true); }}>
          ➕ Add Role
        </button>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:12 }}>
        {roles.map(role => (
          <div key={role.id} style={{ ...S.card, borderLeft:`3px solid ${role.color}`, marginBottom:0 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
              <div>
                <span style={S.badge(role.color)}>{role.label}</span>
                {role.is_system && <span style={{ fontSize:10, color:"#4b5563", marginLeft:6 }}>system</span>}
              </div>
              <div style={{ display:"flex", gap:4 }}>
                <button style={{ ...S.btn("#1e3a5f"), padding:"3px 8px", fontSize:10 }}
                  onClick={() => { setEditing(role); setForm({ label:role.label, color:role.color, description:role.description||"", permissions: role.permissions.map(p=>p.id) }); setMsg(""); setShowForm(true); }}>
                  ✏️
                </button>
                {!role.is_system && (
                  <button style={{ ...S.btn("#450a0a"), padding:"3px 8px", fontSize:10 }} onClick={() => setDelete(role)}>🗑</button>
                )}
              </div>
            </div>
            <div style={{ fontSize:11, color:"#64748b", marginBottom:8 }}>{role.description || "No description"}</div>
            <div style={{ fontSize:10, color:"#4b5563", marginBottom:4 }}>PERMISSIONS ({role.permissions?.length || 0})</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
              {role.permissions?.map(p => (
                <span key={p.id} style={{ fontSize:10, padding:"2px 6px", borderRadius:4, background:"rgba(34,197,94,0.1)", color:"#86efac", border:"1px solid rgba(34,197,94,0.2)" }}>
                  {p.label}
                </span>
              ))}
              {(!role.permissions || role.permissions.length === 0) && (
                <span style={{ fontSize:10, color:"#4b5563" }}>No permissions assigned</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Role Modal */}
      {showForm && (
        <Modal title={editing ? "✏️ Edit Role" : "➕ Add New Role"} onClose={() => setShowForm(false)}>
          <label style={S.label}>ROLE LABEL</label>
          <input style={S.input} value={form.label} onChange={e=>setForm(p=>({...p, label:e.target.value}))} placeholder="e.g. Field Engineer" />
          <label style={S.label}>COLOR</label>
          <div style={{ display:"flex", gap:8, marginBottom:12 }}>
            <input style={{ ...S.input, marginBottom:0, flex:1 }} value={form.color} onChange={e=>setForm(p=>({...p, color:e.target.value}))} />
            <input type="color" value={form.color} onChange={e=>setForm(p=>({...p, color:e.target.value}))}
              style={{ width:42, height:38, borderRadius:8, border:"1px solid #1e3a2e", background:"#0a1628", cursor:"pointer", padding:2 }} />
          </div>
          <label style={S.label}>DESCRIPTION</label>
          <input style={S.input} value={form.description} onChange={e=>setForm(p=>({...p, description:e.target.value}))} placeholder="What can this role do?" />

          <label style={S.label}>PERMISSIONS</label>
          {Object.entries(permsByCategory).map(([cat, perms]) => (
            <div key={cat} style={{ marginBottom:12 }}>
              <div style={{ fontSize:10, color:"#4ade80", marginBottom:6, letterSpacing:"0.08em" }}>{cat.toUpperCase()}</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:4 }}>
                {perms.map(p => (
                  <label key={p.id} style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer", fontSize:12, color: form.permissions.includes(p.id) ? "#e2e8f0" : "#64748b" }}>
                    <input type="checkbox" checked={form.permissions.includes(p.id)} onChange={() => togglePerm(p.id)}
                      style={{ accentColor:"#22c55e" }} />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>
          ))}

          <Msg text={msg} />
          <div style={{ display:"flex", gap:8, marginTop:8 }}>
            <button style={{ ...S.btn("#1e2d3d"), flex:1 }} onClick={() => setShowForm(false)}>Cancel</button>
            <button style={{ ...S.btn(), flex:1 }} onClick={save}>{editing ? "Update Role" : "Create Role"}</button>
          </div>
        </Modal>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <Modal title="🗑 Delete Role" onClose={() => setDelete(null)}>
          <p style={{ color:"#94a3b8", fontSize:13, marginBottom:20 }}>
            Delete role <strong style={{ color:"#e2e8f0" }}>{deleteTarget.label}</strong>? Users with this role will have no role assigned.
          </p>
          <div style={{ display:"flex", gap:8 }}>
            <button style={{ ...S.btn("#1e2d3d"), flex:1 }} onClick={() => setDelete(null)}>Cancel</button>
            <button style={{ ...S.btn("#7f1d1d"), flex:1 }} onClick={doDelete}>Yes, Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// GROUPS TAB
// ════════════════════════════════════════════════════════════
function GroupsTab({ allUsers }) {
  const [groups, setGroups]         = useState([]);
  const [showForm, setShowForm]     = useState(false);
  const [editing, setEditing]       = useState(null);
  const [deleteTarget, setDelete]   = useState(null);
  const [addMemberGroup, setAddMemberGroup] = useState(null);
  const [selectedUser, setSelectedUser]     = useState("");
  const [msg, setMsg]               = useState("");
  const emptyForm = { name:"", description:"", color:"#3b82f6" };
  const [form, setForm]             = useState(emptyForm);

  const load = useCallback(() => {
    fetchGroups().then(r => setGroups(r.data.data)).catch(console.error);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setMsg("");
    try {
      if (editing) { await updateGroup(editing.id, form); setMsg("✅ Group updated!"); }
      else         { await createGroup(form);              setMsg("✅ Group created!"); }
      load();
      setTimeout(() => { setShowForm(false); setEditing(null); setForm(emptyForm); setMsg(""); }, 1500);
    } catch(err) { setMsg(err.response?.data?.message || "Failed to save group"); }
  };

  const doDelete = async () => {
    try { await deleteGroup(deleteTarget.id); load(); setDelete(null); }
    catch(err) { setMsg(err.response?.data?.message || "Failed to delete"); }
  };

  const doAddMember = async () => {
    if (!selectedUser) return;
    try { await addGroupMember(addMemberGroup.id, selectedUser); load(); setSelectedUser(""); }
    catch(err) { console.error(err); }
  };

  const doRemoveMember = async (groupId, userId) => {
    try { await removeGroupMember(groupId, userId); load(); }
    catch(err) { console.error(err); }
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, color:"#4ade80" }}>👥 Groups</div>
          <div style={{ fontSize:11, color:"#4b5563" }}>{groups.length} groups configured</div>
        </div>
        <button style={S.btn()} onClick={() => { setEditing(null); setForm(emptyForm); setMsg(""); setShowForm(true); }}>
          ➕ Add Group
        </button>
      </div>

      {groups.map(group => (
        <div key={group.id} style={{ ...S.card, borderLeft:`3px solid ${group.color}` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
            <div>
              <span style={{ fontWeight:700, fontSize:14, color:"#e2e8f0" }}>{group.name}</span>
              <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>{group.description || "No description"}</div>
            </div>
            <div style={{ display:"flex", gap:4 }}>
              <button style={{ ...S.btn("#1e3a5f"), padding:"4px 8px", fontSize:10 }}
                onClick={() => { setAddMemberGroup(group); setSelectedUser(""); }}>
                ➕ Member
              </button>
              <button style={{ ...S.btn("#1e3a5f"), padding:"4px 8px", fontSize:10 }}
                onClick={() => { setEditing(group); setForm({ name:group.name, description:group.description||"", color:group.color }); setMsg(""); setShowForm(true); }}>
                ✏️
              </button>
              <button style={{ ...S.btn("#450a0a"), padding:"4px 8px", fontSize:10 }}
                onClick={() => setDelete(group)}>🗑</button>
            </div>
          </div>

          <div style={{ fontSize:10, color:"#4b5563", marginBottom:6 }}>MEMBERS ({group.members?.length || 0})</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {group.members?.map(m => (
              <div key={m.id} style={{ display:"flex", alignItems:"center", gap:4, background:"rgba(255,255,255,0.04)", border:"1px solid #1e2d3d", borderRadius:99, padding:"3px 8px" }}>
                <span style={{ fontSize:11, color:"#e2e8f0" }}>{m.full_name}</span>
                {m.role_label && <span style={S.badge(m.role_color||"#22c55e")}>{m.role_label}</span>}
                <button onClick={() => doRemoveMember(group.id, m.id)}
                  style={{ background:"transparent", border:"none", color:"#f87171", cursor:"pointer", fontSize:12, padding:"0 2px" }}>×</button>
              </div>
            ))}
            {(!group.members || group.members.length === 0) && (
              <span style={{ fontSize:11, color:"#4b5563" }}>No members yet</span>
            )}
          </div>
        </div>
      ))}

      {/* Add/Edit Group Modal */}
      {showForm && (
        <Modal title={editing ? "✏️ Edit Group" : "➕ Add New Group"} onClose={() => setShowForm(false)}>
          <label style={S.label}>GROUP NAME</label>
          <input style={S.input} value={form.name} onChange={e=>setForm(p=>({...p, name:e.target.value}))} placeholder="e.g. North Zone Team" />
          <label style={S.label}>DESCRIPTION</label>
          <input style={S.input} value={form.description} onChange={e=>setForm(p=>({...p, description:e.target.value}))} placeholder="What is this group for?" />
          <label style={S.label}>COLOR</label>
          <div style={{ display:"flex", gap:8, marginBottom:12 }}>
            <input style={{ ...S.input, marginBottom:0, flex:1 }} value={form.color} onChange={e=>setForm(p=>({...p, color:e.target.value}))} />
            <input type="color" value={form.color} onChange={e=>setForm(p=>({...p, color:e.target.value}))}
              style={{ width:42, height:38, borderRadius:8, border:"1px solid #1e3a2e", background:"#0a1628", cursor:"pointer", padding:2 }} />
          </div>
          <Msg text={msg} />
          <div style={{ display:"flex", gap:8 }}>
            <button style={{ ...S.btn("#1e2d3d"), flex:1 }} onClick={() => setShowForm(false)}>Cancel</button>
            <button style={{ ...S.btn(), flex:1 }} onClick={save}>{editing ? "Update Group" : "Create Group"}</button>
          </div>
        </Modal>
      )}

      {/* Add Member Modal */}
      {addMemberGroup && (
        <Modal title={`➕ Add Member to ${addMemberGroup.name}`} onClose={() => setAddMemberGroup(null)}>
          <label style={S.label}>SELECT USER</label>
          <select style={S.select} value={selectedUser} onChange={e=>setSelectedUser(e.target.value)}>
            <option value="">— Select User —</option>
            {allUsers
              .filter(u => !addMemberGroup.members?.find(m => m.id === u.id))
              .map(u => <option key={u.id} value={u.id}>{u.full_name} (@{u.username})</option>)
            }
          </select>
          <div style={{ display:"flex", gap:8 }}>
            <button style={{ ...S.btn("#1e2d3d"), flex:1 }} onClick={() => setAddMemberGroup(null)}>Cancel</button>
            <button style={{ ...S.btn(), flex:1 }} onClick={doAddMember}>Add Member</button>
          </div>
        </Modal>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <Modal title="🗑 Delete Group" onClose={() => setDelete(null)}>
          <p style={{ color:"#94a3b8", fontSize:13, marginBottom:20 }}>
            Delete group <strong style={{ color:"#e2e8f0" }}>{deleteTarget.name}</strong>? Members will not be deleted, just removed from group.
          </p>
          <div style={{ display:"flex", gap:8 }}>
            <button style={{ ...S.btn("#1e2d3d"), flex:1 }} onClick={() => setDelete(null)}>Cancel</button>
            <button style={{ ...S.btn("#7f1d1d"), flex:1 }} onClick={doDelete}>Yes, Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════
// POLE TYPES TAB
// ════════════════════════════════════════════════════════════
function PoleTypesTab() {
  const [types, setTypes]         = useState([]);
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [deleteTarget, setDelete] = useState(null);
  const [msg, setMsg]             = useState("");
  const emptyForm = { label:"", code:"", description:"", icon:"🪵", sort_order:"0" };
  const [form, setForm]           = useState(emptyForm);

  const load = useCallback(() => {
    fetchPoleTypes().then(r => setTypes(r.data.data)).catch(console.error);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setMsg("");
    if (!form.label || !form.code) return setMsg("Code and label are required");
    try {
      if (editing) { await updatePoleType(editing.id, form); setMsg("✅ Pole type updated!"); }
      else         { await createPoleType(form);              setMsg("✅ Pole type created!"); }
      load();
      setTimeout(() => { setShowForm(false); setEditing(null); setForm(emptyForm); setMsg(""); }, 1500);
    } catch(err) { setMsg(err.response?.data?.message || "Failed to save"); }
  };

  const doDelete = async () => {
    try { await deletePoleType(deleteTarget.id); load(); setDelete(null); }
    catch(err) { setMsg(err.response?.data?.message || "Failed to delete"); }
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, color:"#4ade80" }}>🪵 Pole Types</div>
          <div style={{ fontSize:11, color:"#4b5563" }}>{types.length} types configured</div>
        </div>
        <button style={S.btn()} onClick={() => { setEditing(null); setForm(emptyForm); setMsg(""); setShowForm(true); }}>
          ➕ Add Type
        </button>
      </div>

      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:"#060c12" }}>
              {["Icon","Label","Code","Description","Order","Status","Actions"].map(h=>(
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {types.map((t,i) => (
              <tr key={t.id} style={{ background: i%2===0 ? "rgba(255,255,255,0.01)" : "transparent" }}>
                <td style={S.td}><span style={{ fontSize:20 }}>{t.icon}</span></td>
                <td style={S.td}><span style={{ fontWeight:700, color:"#e2e8f0" }}>{t.label}</span></td>
                <td style={S.td}><span style={{ color:"#64748b", fontSize:11 }}>{t.code}</span></td>
                <td style={S.td}><span style={{ color:"#4b5563", fontSize:11 }}>{t.description || "—"}</span></td>
                <td style={S.td}><span style={{ color:"#94a3b8" }}>{t.sort_order}</span></td>
                <td style={S.td}>
                  <span style={S.badge(t.is_active ? "#22c55e" : "#6b7280")}>
                    {t.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td style={S.td}>
                  <div style={{ display:"flex", gap:4 }}>
                    <button style={{ ...S.btn("#1e3a5f"), padding:"4px 8px", fontSize:10 }}
                      onClick={() => { setEditing(t); setForm({ label:t.label, code:t.code, description:t.description||"", icon:t.icon||"🪵", sort_order:t.sort_order||"0", is_active:t.is_active }); setMsg(""); setShowForm(true); }}>
                      ✏️ Edit
                    </button>
                    <button style={{ ...S.btn("#450a0a"), padding:"4px 8px", fontSize:10 }}
                      onClick={() => setDelete(t)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {types.length === 0 && <div style={{ textAlign:"center", color:"#4b5563", padding:32, fontSize:13 }}>No pole types yet</div>}
      </div>

      {showForm && (
        <Modal title={editing ? "✏️ Edit Pole Type" : "➕ Add Pole Type"} onClose={() => setShowForm(false)}>
          <div style={{ display:"flex", gap:10 }}>
            <div style={{ flex:1 }}>
              <label style={S.label}>LABEL</label>
              <input style={S.input} value={form.label} onChange={e=>setForm(p=>({...p, label:e.target.value}))} placeholder="e.g. Large Concrete Pole" />
            </div>
            <div style={{ width:70 }}>
              <label style={S.label}>ICON</label>
              <input style={{ ...S.input, textAlign:"center", fontSize:20 }} value={form.icon} onChange={e=>setForm(p=>({...p, icon:e.target.value}))} />
            </div>
          </div>
          <label style={S.label}>CODE (no spaces)</label>
          <input style={S.input} value={form.code} onChange={e=>setForm(p=>({...p, code:e.target.value.toLowerCase().replace(/\s+/g,"_")}))} placeholder="e.g. large_concrete" disabled={!!editing} />
          <label style={S.label}>DESCRIPTION</label>
          <input style={S.input} value={form.description} onChange={e=>setForm(p=>({...p, description:e.target.value}))} placeholder="Brief description..." />
          <label style={S.label}>SORT ORDER</label>
          <input style={S.input} type="number" value={form.sort_order} onChange={e=>setForm(p=>({...p, sort_order:e.target.value}))} />
          {editing && (<>
            <label style={S.label}>STATUS</label>
            <select style={S.select} value={form.is_active ? "1":"0"} onChange={e=>setForm(p=>({...p, is_active:e.target.value==="1"}))}>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </>)}
          <Msg text={msg} />
          <div style={{ display:"flex", gap:8 }}>
            <button style={{ ...S.btn("#1e2d3d"), flex:1 }} onClick={() => setShowForm(false)}>Cancel</button>
            <button style={{ ...S.btn(), flex:1 }} onClick={save}>{editing ? "Update" : "Create"}</button>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <Modal title="🗑 Delete Pole Type" onClose={() => setDelete(null)}>
          <p style={{ color:"#94a3b8", fontSize:13, marginBottom:20 }}>
            Delete <strong style={{ color:"#e2e8f0" }}>{deleteTarget.label}</strong>? Poles using this type will have no type assigned.
          </p>
          <div style={{ display:"flex", gap:8 }}>
            <button style={{ ...S.btn("#1e2d3d"), flex:1 }} onClick={() => setDelete(null)}>Cancel</button>
            <button style={{ ...S.btn("#7f1d1d"), flex:1 }} onClick={doDelete}>Yes, Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// MAIN ADMIN DASHBOARD
// ════════════════════════════════════════════════════════════
export default function AdminDashboard() {
  const navigate    = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const [tab, setTab]     = useState("users");
  const [roles, setRoles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    if (currentUser.role !== "admin") { navigate("/"); return; }
    fetchUsers().then(r => setAllUsers(r.data.data)).catch(console.error);
    fetchRoles().then(r => setRoles(r.data.data)).catch(console.error);
  }, []);

  const TABS = [
    { key:"users",      label:"👥 Users" },
    { key:"roles",      label:"🔑 Roles & Permissions" },
    { key:"groups",     label:"🏢 Groups" },
    { key:"poletypes",  label:"🪵 Pole Types" },
  ];

  return (
    <div style={S.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=Syne:wght@700;800&display=swap');`}</style>

      {/* Top Nav */}
      <div style={{ background:"linear-gradient(135deg,#0d2010,#0a1628)", borderBottom:"1px solid #1a2e1a", padding:"12px 24px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={() => navigate("/dashboard")}
              style={{ background:"transparent", border:"1px solid #1e3a2e", color:"#4ade80", borderRadius:8, padding:"6px 12px", fontSize:11, cursor:"pointer" }}>
              ← Dashboard
            </button>
            <button onClick={() => navigate("/")}
              style={{ background:"transparent", border:"1px solid #1e3a2e", color:"#94a3b8", borderRadius:8, padding:"6px 12px", fontSize:11, cursor:"pointer" }}>
              🗺️ Map
            </button>
          </div>
          <div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:18, fontWeight:800, color:"#4ade80" }}>⚡ Admin Dashboard</div>
            <div style={{ fontSize:10, color:"#4b5563", letterSpacing:"0.1em" }}>CEB SOLAR POLE TRACKER</div>
          </div>
        </div>
        <div style={{ fontSize:12, color:"#64748b" }}>
          Logged in as <span style={{ color:"#4ade80", fontWeight:700 }}>{currentUser.full_name}</span>
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{ background:"#0d1520", borderBottom:"1px solid #1a2e1a", padding:"0 24px", display:"flex", gap:0 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ background:"transparent", border:"none", borderBottom:`2px solid ${tab===t.key ? "#4ade80" : "transparent"}`,
              color: tab===t.key ? "#4ade80" : "#64748b", padding:"14px 20px", fontSize:12, fontWeight: tab===t.key ? 700 : 400, cursor:"pointer", transition:"all 0.2s" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding:24, maxWidth:1200, margin:"0 auto" }}>
        {tab === "users"  && <UsersTab  roles={roles} allUsers={allUsers} />}
        {tab === "roles"  && <RolesTab  onRolesChange={setRoles} />}
        {tab === "groups"    && <GroupsTab allUsers={allUsers} />}
        {tab === "poletypes" && <PoleTypesTab />}
      </div>
    </div>
  );
}
